/**
 * API Key Storage Service
 * Provides secure storage for AI provider API keys using Web Crypto API for encryption.
 * Ciphertexts live in localStorage; the AES-GCM key lives in IndexedDB as a
 * NON-EXTRACTABLE CryptoKey — scripts can encrypt/decrypt with it, but the raw
 * key material can never be read out, so exfiltrated storage is useless off-device.
 */

export type AIProvider = 'gemini' | 'openai' | 'anthropic' | 'custom';

export interface AIProviderConfig {
  id: AIProvider;
  name: string;
  description: string;
  docsUrl: string;
  keyPlaceholder: string;
  keyPrefix?: string; // e.g., 'sk-' for OpenAI
  /** True when the provider also needs a base URL + model (OpenAI-compatible custom). */
  isCustom?: boolean;
}

export const AI_PROVIDERS: Record<AIProvider, AIProviderConfig> = {
  gemini: {
    id: 'gemini',
    name: 'Google Gemini',
    description: 'Google\'s most capable AI model for generating tech stack diagrams',
    docsUrl: 'https://aistudio.google.com/apikey',
    keyPlaceholder: 'AIza...',
  },
  openai: {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT-4 and other OpenAI models for generating tech stack diagrams',
    docsUrl: 'https://platform.openai.com/api-keys',
    keyPlaceholder: 'sk-...',
    keyPrefix: 'sk-',
  },
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic Claude',
    description: 'Claude AI models for generating tech stack diagrams',
    docsUrl: 'https://console.anthropic.com/settings/keys',
    keyPlaceholder: 'sk-ant-...',
    keyPrefix: 'sk-ant-',
  },
  custom: {
    id: 'custom',
    name: 'OpenRouter / Custom',
    description: 'Any OpenAI-compatible endpoint (OpenRouter, Together, LM Studio, a local proxy). Save the models you use and switch between them in the editor.',
    docsUrl: 'https://openrouter.ai/keys',
    keyPlaceholder: 'sk-or-v1-...',
    isCustom: true,
  },
};

// Storage keys
const STORAGE_PREFIX = 'stacksketch_api_key_';
const ENCRYPTION_KEY_NAME = 'stacksketch_encryption_key';
const DEFAULT_PROVIDER_KEY = 'stacksketch_default_provider';
const CUSTOM_BASE_URL_KEY = 'stacksketch_custom_base_url';
const CUSTOM_MODEL_KEY = 'stacksketch_custom_model'; // legacy single-model slot, read during migration
const CUSTOM_MODELS_KEY = 'stacksketch_custom_models';
const CUSTOM_SELECTED_MODEL_KEY = 'stacksketch_custom_selected_model';

// Default base URL for the custom provider — OpenRouter, so testing it only needs
// a key + a model id. Override for any other OpenAI-compatible endpoint.
export const DEFAULT_CUSTOM_BASE_URL = 'https://openrouter.ai/api/v1';

/** Base URL for the custom OpenAI-compatible provider (not a secret — plain storage). */
export function getCustomBaseUrl(): string {
  return localStorage.getItem(CUSTOM_BASE_URL_KEY) || DEFAULT_CUSTOM_BASE_URL;
}
export function setCustomBaseUrl(url: string): void {
  localStorage.setItem(CUSTOM_BASE_URL_KEY, url.trim());
}

/**
 * Saved model ids for the custom provider (e.g. OpenRouter models like
 * 'z-ai/glm-4.6', 'deepseek/deepseek-chat'), in add order. First call migrates
 * the legacy single-model slot into the list.
 */
export function getCustomModels(): string[] {
  const raw = localStorage.getItem(CUSTOM_MODELS_KEY);
  if (raw === null) {
    const legacy = localStorage.getItem(CUSTOM_MODEL_KEY)?.trim() || '';
    const models = legacy ? [legacy] : [];
    localStorage.setItem(CUSTOM_MODELS_KEY, JSON.stringify(models));
    if (legacy) localStorage.setItem(CUSTOM_SELECTED_MODEL_KEY, legacy);
    return models;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((m): m is string => typeof m === 'string' && m.trim() !== '');
  } catch {
    return [];
  }
}

function storeCustomModels(models: string[]): void {
  localStorage.setItem(CUSTOM_MODELS_KEY, JSON.stringify(models));
}

/** Add a model id to the custom provider's saved list (no-op when already present). */
export function addCustomModel(model: string): void {
  const id = model.trim();
  if (!id) return;
  const models = getCustomModels();
  if (!models.includes(id)) storeCustomModels([...models, id]);
}

/** Remove a model id from the saved list; re-points the selection when needed. */
export function removeCustomModel(model: string): void {
  const models = getCustomModels().filter((m) => m !== model);
  storeCustomModels(models);
  if (localStorage.getItem(CUSTOM_SELECTED_MODEL_KEY) === model) {
    if (models[0]) localStorage.setItem(CUSTOM_SELECTED_MODEL_KEY, models[0]);
    else localStorage.removeItem(CUSTOM_SELECTED_MODEL_KEY);
  }
}

/** The active custom-provider model ('' when nothing is saved). */
export function getSelectedCustomModel(): string {
  const selected = localStorage.getItem(CUSTOM_SELECTED_MODEL_KEY);
  if (selected && getCustomModels().includes(selected)) return selected;
  return getCustomModels()[0] || '';
}

/** Make a model the active one; also saves it to the list when new. */
export function setSelectedCustomModel(model: string): void {
  const id = model.trim();
  if (!id) return;
  addCustomModel(id);
  localStorage.setItem(CUSTOM_SELECTED_MODEL_KEY, id);
}

/** Active model id for the custom provider (e.g. an OpenRouter model like 'openai/gpt-4o'). */
export function getCustomModel(): string {
  return getSelectedCustomModel();
}
export function setCustomModel(model: string): void {
  setSelectedCustomModel(model);
}

// Per-provider selected model. Custom routes to the shared model list.
const MODEL_KEY_PREFIX = 'stacksketch_model_';

/** The user's chosen model for a provider ('' → fall back to the built-in default). */
export function getSelectedModel(provider: AIProvider): string {
  if (provider === 'custom') return getCustomModel();
  return localStorage.getItem(MODEL_KEY_PREFIX + provider) || '';
}

export function setSelectedModel(provider: AIProvider, model: string): void {
  if (provider === 'custom') {
    setSelectedCustomModel(model);
    return;
  }
  localStorage.setItem(MODEL_KEY_PREFIX + provider, model.trim());
}

// --- Encryption key management ---------------------------------------------
// The key is generated non-extractable and persisted in IndexedDB via
// structured clone. Legacy installs kept an extractable JWK in localStorage
// (ENCRYPTION_KEY_NAME) beside the ciphertexts it protected; on first use we
// re-encrypt every stored API key under the IndexedDB key and delete the JWK.

const IDB_NAME = 'stacksketch-crypto';
const IDB_STORE = 'keys';
const IDB_KEY_ID = 'api-key-encryption-key';

function openKeyDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) {
        req.result.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Get the stored key, or claim the slot with `fresh` — a single readwrite
// transaction, so concurrent tabs always settle on the same key.
function getOrStoreKey(db: IDBDatabase, fresh: CryptoKey): Promise<CryptoKey> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    const getReq = store.get(IDB_KEY_ID);
    let result = fresh;
    getReq.onsuccess = () => {
      const existing = getReq.result as CryptoKey | undefined;
      if (existing) {
        result = existing;
      } else {
        store.put(fresh, IDB_KEY_ID);
      }
    };
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

// Legacy path, kept only as a fallback for environments without IndexedDB:
// an extractable key stored as JWK in localStorage.
async function getOrCreateLocalStorageKey(): Promise<CryptoKey> {
  const storedKey = localStorage.getItem(ENCRYPTION_KEY_NAME);

  if (storedKey) {
    try {
      return await crypto.subtle.importKey(
        'jwk',
        JSON.parse(storedKey),
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
    } catch {
      // If import fails, generate a new key
      localStorage.removeItem(ENCRYPTION_KEY_NAME);
    }
  }

  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const exportedKey = await crypto.subtle.exportKey('jwk', key);
  localStorage.setItem(ENCRYPTION_KEY_NAME, JSON.stringify(exportedKey));
  return key;
}

// One legacy ciphertext at a time: decrypt with the localStorage-era key and
// re-encrypt under the IndexedDB key, then drop the JWK. A value that fails
// to decrypt is cleared, same as the read path.
async function migrateLegacyCiphertexts(newKey: CryptoKey): Promise<void> {
  const storedJwk = localStorage.getItem(ENCRYPTION_KEY_NAME);
  if (!storedJwk) return;

  try {
    const legacyKey = await crypto.subtle.importKey(
      'jwk',
      JSON.parse(storedJwk),
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );
    for (const provider of Object.keys(AI_PROVIDERS) as AIProvider[]) {
      const slot = `${STORAGE_PREFIX}${provider}`;
      const ciphertext = localStorage.getItem(slot);
      if (!ciphertext) continue;
      try {
        const plaintext = await decryptWith(legacyKey, ciphertext);
        localStorage.setItem(slot, await encryptWith(newKey, plaintext));
      } catch {
        localStorage.removeItem(slot);
      }
    }
  } catch {
    // Unreadable legacy key — its ciphertexts are already lost; the read
    // path clears them lazily.
  }
  localStorage.removeItem(ENCRYPTION_KEY_NAME);
}

let keyPromise: Promise<CryptoKey> | null = null;

function getOrCreateEncryptionKey(): Promise<CryptoKey> {
  if (!keyPromise) {
    keyPromise = initEncryptionKey().catch((err) => {
      keyPromise = null;
      throw err;
    });
  }
  return keyPromise;
}

async function initEncryptionKey(): Promise<CryptoKey> {
  let db: IDBDatabase;
  try {
    db = await openKeyDb();
  } catch {
    return getOrCreateLocalStorageKey();
  }
  try {
    const fresh = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false, // non-extractable: the raw key can never leave the browser
      ['encrypt', 'decrypt']
    );
    const key = await getOrStoreKey(db, fresh);
    await migrateLegacyCiphertexts(key);
    return key;
  } finally {
    db.close();
  }
}

async function encryptWith(key: CryptoKey, value: string): Promise<string> {
  const data = new TextEncoder().encode(value);

  // Generate a random IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);

  // Combine IV and encrypted data for storage
  const combined = new Uint8Array(iv.length + encryptedData.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encryptedData), iv.length);

  // Convert to base64 for storage
  return btoa(String.fromCharCode(...combined));
}

async function decryptWith(key: CryptoKey, encryptedValue: string): Promise<string> {
  // Convert from base64
  const combined = new Uint8Array(
    atob(encryptedValue).split('').map(c => c.charCodeAt(0))
  );

  // Extract IV and data
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);

  const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);

  return new TextDecoder().decode(decryptedData);
}

// Encrypt a string value
async function encrypt(value: string): Promise<string> {
  return encryptWith(await getOrCreateEncryptionKey(), value);
}

/**
 * Store an API key for a specific provider (encrypted)
 */
export async function setApiKey(provider: AIProvider, apiKey: string): Promise<void> {
  const encrypted = await encrypt(apiKey);
  localStorage.setItem(`${STORAGE_PREFIX}${provider}`, encrypted);
}

/**
 * Retrieve an API key for a specific provider (decrypted)
 */
export async function getApiKey(provider: AIProvider): Promise<string | null> {
  // Resolve the key first: on legacy installs this runs the one-time
  // migration, which rewrites the ciphertext we're about to read.
  const key = await getOrCreateEncryptionKey();
  const encrypted = localStorage.getItem(`${STORAGE_PREFIX}${provider}`);
  if (!encrypted) return null;

  try {
    return await decryptWith(key, encrypted);
  } catch {
    // If decryption fails (e.g., key was regenerated), clear the invalid data
    localStorage.removeItem(`${STORAGE_PREFIX}${provider}`);
    return null;
  }
}

/**
 * Remove an API key for a specific provider
 */
export function removeApiKey(provider: AIProvider): void {
  localStorage.removeItem(`${STORAGE_PREFIX}${provider}`);
}

/**
 * Check if an API key exists for a specific provider
 */
export function hasApiKey(provider: AIProvider): boolean {
  return localStorage.getItem(`${STORAGE_PREFIX}${provider}`) !== null;
}

/**
 * Get all configured providers (providers with stored API keys)
 */
export function getConfiguredProviders(): AIProvider[] {
  return (Object.keys(AI_PROVIDERS) as AIProvider[]).filter(hasApiKey);
}

/**
 * Get the first configured provider (for default selection)
 */
export function getDefaultProvider(): AIProvider | null {
  const stored = localStorage.getItem(DEFAULT_PROVIDER_KEY);
  if (stored && hasApiKey(stored as AIProvider)) {
    return stored as AIProvider;
  }

  const configured = getConfiguredProviders();
  if (configured.includes('gemini')) return 'gemini';
  return configured[0] || null;
}

/**
 * Set the default provider
 */
export function setDefaultProvider(provider: AIProvider): void {
  localStorage.setItem(DEFAULT_PROVIDER_KEY, provider);
}

/**
 * Get the stored default provider (without fallback)
 */
export function getStoredDefaultProvider(): AIProvider | null {
  const stored = localStorage.getItem(DEFAULT_PROVIDER_KEY);
  return stored as AIProvider | null;
}

/**
 * Validate API key format (basic validation)
 */
export function validateApiKeyFormat(provider: AIProvider, apiKey: string): boolean {
  if (!apiKey || apiKey.trim().length === 0) return false;

  const config = AI_PROVIDERS[provider];

  // Custom OpenAI-compatible endpoints vary wildly (OpenRouter keys, or a dummy
  // token for a local proxy), so only require a non-empty value.
  if (config.isCustom) return true;

  // Check prefix if defined
  if (config.keyPrefix && !apiKey.startsWith(config.keyPrefix)) {
    return false;
  }

  // Basic length check (API keys are typically > 20 chars)
  if (apiKey.length < 20) return false;

  return true;
}

/**
 * Mask an API key for display (show first and last 4 chars)
 */
export function maskApiKey(apiKey: string): string {
  if (apiKey.length <= 12) {
    return '*'.repeat(apiKey.length);
  }
  return `${apiKey.slice(0, 4)}${'*'.repeat(apiKey.length - 8)}${apiKey.slice(-4)}`;
}



